<template>
  <CatalogoPagina
    titulo="Clientes"
    prefijo="clientes"
    :resumen="resumenClientes"
    :columnas="columnas"
    :total="totalClientes"
    :pagina="paginaActual"
    :total-paginas="totalPaginas"
    :limite="limite"
    :cargando="cargando"
    :ya-cargo="yaCargo"
    :error="errorCarga"
    mensaje-error="No pudimos cargar los clientes."
    :orden-campo="ordenCampo"
    :orden-asc="ordenAsc"
    :aviso="aviso"
    @limite="alCambiarLimite"
    @ordenar="ordenarPor"
    @pagina="cambiarPagina"
    @reintentar="initialize"
    @cerrar-aviso="aviso = ''"
  >
    <template #acciones>
      <button type="button" class="rm-btn rm-btn-primario" data-testid="clientes-nuevo" @click="nuevoCliente">
        <AppIcon name="plus" size="h-4 w-4" />
        Nuevo cliente
      </button>
    </template>

    <template #filtros>
      <AppBusqueda
        v-model="busqueda"
        placeholder="Buscar clientes"
        testid="clientes-buscar"
        @buscar="aplicarFiltros"
      />

        <div class="cat-filtro-grupo cat-filtro-servicios">
          <span class="sr-only">Servicios</span>
          <div class="cat-segmentos" role="group" aria-label="Filtrar por servicios">
            <button
              v-for="opcion in opcionesServicios"
              :key="opcion.value"
              type="button"
              :class="{ seleccionado: filtroServicios === opcion.value }"
              :title="opcion.label"
              :aria-label="opcion.label"
              :aria-pressed="filtroServicios === opcion.value ? 'true' : 'false'"
              :aria-describedby="'tooltip-servicios-' + opcion.value"
              :data-testid="'filtro-servicios-' + opcion.value"
              @click="cambiarFiltroServicios(opcion.value)"
            >
              <AppIcon :name="opcion.icon" size="h-4 w-4" />
              <span
                :id="'tooltip-servicios-' + opcion.value"
                class="cat-tooltip"
                role="tooltip"
              >{{ opcion.label }}</span>
            </button>
          </div>
        </div>

        <DateRangeFilter
          class="cat-filtro-fechas"
          label="Última visita"
          :desde="visitaDesde"
          :hasta="visitaHasta"
          :opciones="rangosVisita"
          testid="filtro-visita"
          @change="cambiarRangoVisita"
        />

        <button
          v-if="hayFiltros"
          type="button"
          class="cat-limpiar"
          data-testid="clientes-limpiar-filtros"
          @click="limpiarFiltros"
        >
          Limpiar filtros
        </button>
    </template>

    <template #filas="{ columnaVisible }">
      <tr v-for="cliente in clientes" :key="cliente._id" data-testid="cliente-fila">
        <td class="cat-celda-cliente">
          <div class="cat-nombre">
            <span class="cat-avatar" aria-hidden="true">{{ iniciales(cliente) }}</span>
            <div class="cat-nombre-datos">
              <span class="cat-nombre-texto">{{ nombreCompleto(cliente) }}</span>
              <span v-if="cliente.rfc" class="cat-meta">{{ cliente.rfc }}</span>
            </div>
          </div>
        </td>
        <td class="cat-celda-contacto">
          <!-- El flex va en un div: un <td> con display:flex deja de ser celda -->
          <div class="cat-pila">
            <span class="cat-telefono">{{ telefonoDe(cliente) }}</span>
            <span v-if="cliente.email" class="cat-meta">{{ cliente.email }}</span>
          </div>
        </td>
        <td v-if="columnaVisible('address')" class="cat-celda-direccion">
          <div v-if="direccionDe(cliente)" class="cat-pila">
            <span class="cat-direccion-linea">{{ direccionDe(cliente).linea1 }}</span>
            <span v-if="direccionDe(cliente).linea2" class="cat-meta cat-direccion-linea">
              {{ direccionDe(cliente).linea2 }}
            </span>
          </div>
          <span v-else class="cat-sin-dato" aria-label="Sin dirección">—</span>
        </td>
        <td class="cat-celda-actividad">
          <div v-if="cliente.autos || cliente.ultima_visita" class="cat-pila">
            <span :class="{ 'cat-sin-dato': !cliente.autos }">{{ textoAutos(cliente.autos) }}</span>
            <span
              v-if="cliente.ultima_visita"
              class="cat-meta cat-visita"
              :title="'Última visita ' + ultimaVisitaDe(cliente)"
              :aria-label="'Última visita ' + ultimaVisitaDe(cliente)"
            >
              <AppIcon name="clock" size="h-3.5 w-3.5" />
              {{ ultimaVisitaDe(cliente) }}
            </span>
          </div>
          <span v-else class="cat-sin-dato" aria-label="Sin autos ni visitas">—</span>
        </td>
        <td class="cat-celda-alta">{{ fechaAltaDe(cliente) }}</td>
        <td class="cat-fila-acciones">
          <button
            type="button"
            class="cat-icono-btn"
            :aria-label="'Editar a ' + nombreCompleto(cliente)"
            data-testid="cliente-editar"
            @click="editarCliente(cliente)"
          >
            <AppIcon name="pencil" size="h-4 w-4" />
          </button>
          <button
            type="button"
            class="cat-icono-btn peligro"
            :aria-label="'Eliminar a ' + nombreCompleto(cliente)"
            data-testid="cliente-eliminar"
            @click="pedirEliminar(cliente)"
          >
            <AppIcon name="trash" size="h-4 w-4" />
          </button>
        </td>
      </tr>
    </template>

    <template #vacio>
      <AppIcon :name="hayFiltros ? 'search' : 'users'" size="h-8 w-8" />
      <p v-if="filtrosIncompatibles">
        Los clientes sin servicios no tienen visitas: quita uno de los dos filtros.
      </p>
      <p v-else-if="hayFiltros">No hay clientes que coincidan con los filtros.</p>
      <template v-else>
        <p>Aún no hay clientes registrados.</p>
        <button type="button" class="rm-btn rm-btn-primario" @click="nuevoCliente">
          Registrar al primer cliente
        </button>
      </template>
    </template>

    <ClienteFormModal
      :visible="dialogoForm"
      :cliente="clienteSeleccionado"
      :saving="guardando"
      @close="cerrarFormulario"
      @save="guardarCliente"
    />

    <BaseModal :visible="dialogoEliminar" title="Eliminar cliente" @close="cerrarEliminar">
      <p class="cat-confirmacion">
        ¿Seguro de eliminar a <b>{{ nombreCompleto(clienteSeleccionado) }}</b>? Esta acción no se puede deshacer.
      </p>
      <template #footer>
        <button type="button" class="rm-btn rm-btn-secundario" @click="cerrarEliminar">Cancelar</button>
        <button
          type="button"
          class="rm-btn rm-btn-peligro"
          :disabled="guardando"
          data-testid="cliente-confirmar-eliminar"
          @click="confirmarEliminar"
        >
          {{ guardando ? 'Eliminando…' : 'Eliminar' }}
        </button>
      </template>
    </BaseModal>
  </CatalogoPagina>
</template>
<script>
/*
 * Clientes — primer catálogo migrado a Tailwind (fase 4 de MIGRACION.md).
 *
 * Reemplaza v-data-table/v-dialog/v-text-field de Vuetify y los Swal de
 * éxito por tabla HTML, BaseModal y un aviso en línea con auto-cierre.
 * La búsqueda, filtros, orden y paginación se resuelven en Mongo mediante
 * /api/client/paginated; el navegador recibe solo los registros visibles.
 * En escritorio el tamaño de página se calcula con las medidas reales para
 * que la tabla llene la pantalla sin scroll (6 filas en 1280×720, hasta 10).
 * Conserva la misma regla de Guardar (en ClienteFormModal) y la normalización
 * de teléfono para registros históricos.
 *
 * Mejoras respecto a la versión vieja (documentadas para la remisión):
 * - Si la carga falla, muestra estado de error con Reintentar (antes la
 *   tabla quedaba vacía sin aviso, porque el servicio devuelve undefined).
 * - Teléfonos guardados como texto se muestran completos (antes la celda
 *   tomaba telephones[0] y un string mostraba solo el primer dígito) y
 *   con formato (667) 319 0560 o (55) 1234 5678 según la lada.
 * - Nombres capitalizados aunque estén guardados en mayúsculas o minúsculas.
 * - Dirección en dos líneas (calle / colonia y ciudad); si el cliente solo
 *   tiene domicilio fiscal, se arma con él. Sin dato: un guion tenue en vez
 *   de repetir "Sin dirección", "Sin correo" o "Sin RFC" en cada fila.
 * - Fecha de alta numérica (dd/mm/aaaa) en la última columna de datos, en
 *   hora local (antes un alta después de las 5 pm salía al día siguiente).
 * - Autos registrados y última visita al taller de cada cliente.
 * - Filtro de última visita con rangos pensados para el taller: quién vino
 *   (30 días, 3 y 6 meses) y quién no ha vuelto (más de 6 meses, de 1 año),
 *   además del rango personalizado.
 * - El aviso de éxito se muestra dentro de la pantalla (antes Swal tapaba).
 */
import ApiService from '@/services/apis'
import AppBusqueda from '@/components/ui/AppBusqueda.vue'
import AppIcon from '@/components/ui/AppIcon.vue'
import BaseModal from '@/components/ui/BaseModal.vue'
import CatalogoPagina, { LIMITE_BASE } from '@/components/catalogo/CatalogoPagina.vue'
import ClienteFormModal from '@/components/clientes/ClienteFormModal.vue'
import DateRangeFilter from '@/components/ui/DateRangeFilter.vue'
import { RANGOS_VISITA } from '@/dashboard/calendar'
import { capitalizarNombre, direccionDeCliente, formatearFechaNumerica, formatearTelefono } from '@/utils/formato'
import { paginaEquivalente } from '@/utils/paginacion'

const COLUMNAS = [
  { value: 'fullName', label: 'Cliente', ancho: '27%' },
  { value: 'contacto', label: 'Contacto', ancho: '21%' },
  // Con la tabla angosta quedaría en unos cuantos pixeles: mejor ocultarla
  { value: 'address', label: 'Dirección', ocultableAngosto: true },
  // Se calcula por página en el backend; no se puede ordenar por ella
  { value: 'actividad', label: 'Autos y visita', ordenable: false, ancho: '128px' },
  { value: 'createdAt', label: 'Fecha de alta', ancho: '112px', alinear: 'derecha' },
]

const OPCIONES_SERVICIOS = [
  { value: 'todos', label: 'Todos', icon: 'users' },
  { value: 'con', label: 'Con servicios', icon: 'wrench' },
  { value: 'sin', label: 'Sin servicios', icon: 'minus' },
]

function primerTelefono(telefonos) {
  if (typeof telefonos === 'string') return telefonos || 'No disponible'
  return Array.isArray(telefonos) && telefonos.length > 0 ? telefonos[0] : 'No disponible'
}

export default {
  name: 'clientes',
  components: { AppBusqueda, AppIcon, BaseModal, CatalogoPagina, ClienteFormModal, DateRangeFilter },
  data() {
    return {
      columnas: COLUMNAS,
      opcionesServicios: OPCIONES_SERVICIOS,
      clientes: [],
      busqueda: '',
      filtroServicios: 'todos',
      // Quién vino hace poco y quién no ha vuelto (para invitar a servicio)
      rangosVisita: RANGOS_VISITA,
      visitaDesde: '',
      visitaHasta: '',
      ordenCampo: null,
      ordenAsc: false,
      paginaActual: 1,
      totalClientes: 0,
      totalPaginas: 1,
      limite: LIMITE_BASE,
      // Arranca cargando: CatalogoPagina mide el skeleton y emite `limite`
      cargando: true,
      // true tras la primera respuesta buena: desde ahí no se usa el skeleton
      yaCargo: false,
      errorCarga: false,
      guardando: false,
      dialogoForm: false,
      dialogoEliminar: false,
      clienteSeleccionado: null,
      aviso: '',
    }
  },
  computed: {
    filtrosIncompatibles() {
      return this.filtroServicios === 'sin' && Boolean(this.visitaDesde || this.visitaHasta)
    },
    hayFiltros() {
      return Boolean(
        this.busqueda.trim() ||
          this.filtroServicios !== 'todos' ||
          this.visitaDesde ||
          this.visitaHasta
      )
    },
    resumenClientes() {
      return `${this.totalClientes} registrados`
    },
  },
  created() {
    // Contador no reactivo: identifica la consulta más reciente
    this.ultimaConsulta = 0
    this.limiteListo = false
  },
  beforeDestroy() {
    clearTimeout(this.avisoTimer)
  },
  methods: {
    async initialize() {
      const consulta = ++this.ultimaConsulta
      this.cargando = true
      this.errorCarga = false
      try {
        const res = await ApiService.clientePaginado({
          pagina: this.paginaActual,
          limite: this.limite,
          busqueda: this.busqueda.trim(),
          servicios: this.filtroServicios,
          visitaDesde: this.visitaDesde,
          visitaHasta: this.visitaHasta,
          ordenarPor: this.ordenCampo || 'createdAt',
          direccion: this.ordenAsc ? 'asc' : 'desc',
        })
        // Una respuesta que llega tarde (ya hay una búsqueda más nueva) se descarta
        if (consulta !== this.ultimaConsulta) return
        if (!Array.isArray(res?.clientes)) throw new Error('Respuesta inválida del servicio')
        this.clientes = res.clientes
        this.yaCargo = true
        this.totalClientes = Number(res.total) || 0
        this.totalPaginas = Math.max(1, Number(res.totalPages) || 1)
        if (this.paginaActual > this.totalPaginas) {
          this.paginaActual = this.totalPaginas
          return this.initialize()
        }
      } catch (err) {
        if (consulta !== this.ultimaConsulta) return
        this.errorCarga = true
        // Al reintentar se vuelve a mostrar el skeleton, no un "sin clientes" falso
        this.yaCargo = false
        this.clientes = []
        this.totalClientes = 0
      } finally {
        if (consulta === this.ultimaConsulta) this.cargando = false
      }
    },
    // CatalogoPagina mide cuántas filas caben: al montar y al redimensionar
    alCambiarLimite(nuevo) {
      if (!this.limiteListo) {
        this.limiteListo = true
        this.limite = nuevo
        return this.initialize()
      }
      if (nuevo === this.limite) return
      // Conserva visible el primer registro de la página actual
      this.paginaActual = paginaEquivalente(this.paginaActual, this.limite, nuevo)
      this.limite = nuevo
      this.initialize()
    },
    ordenarPor(campo) {
      if (this.ordenCampo === campo) {
        this.ordenAsc = !this.ordenAsc
      } else {
        this.ordenCampo = campo
        this.ordenAsc = true
      }
      this.paginaActual = 1
      this.initialize()
    },
    nombreCompleto(cliente) {
      if (!cliente) return ''
      return capitalizarNombre([cliente.name, cliente.first_lastname, cliente.second_lastname].filter(Boolean).join(' '))
    },
    iniciales(cliente) {
      const partes = [cliente?.name, cliente?.first_lastname].filter(Boolean)
      const iniciales = partes.map((p) => String(p).trim()[0] || '').join('')
      return iniciales ? iniciales.toUpperCase() : '·'
    },
    telefonoDe(cliente) {
      return formatearTelefono(primerTelefono(cliente.telephones))
    },
    direccionDe(cliente) {
      return direccionDeCliente(cliente)
    },
    textoAutos(autos) {
      if (!autos) return 'Sin autos'
      return autos === 1 ? '1 auto' : `${autos} autos`
    },
    ultimaVisitaDe(cliente) {
      // received_at se guarda como fecha sin hora (00:00 UTC)
      return formatearFechaNumerica(cliente.ultima_visita, { utc: true })
    },
    fechaAltaDe(cliente) {
      return formatearFechaNumerica(cliente.createdAt) || '—'
    },
    cambiarFiltroServicios(valor) {
      this.filtroServicios = valor
      this.aplicarFiltros()
    },
    cambiarRangoVisita({ desde, hasta }) {
      if (desde === this.visitaDesde && hasta === this.visitaHasta) return
      this.visitaDesde = desde
      this.visitaHasta = hasta
      this.aplicarFiltros()
    },
    aplicarFiltros() {
      this.paginaActual = 1
      this.initialize()
    },
    cambiarPagina(pagina) {
      if (pagina < 1 || pagina > this.totalPaginas || pagina === this.paginaActual) return
      this.paginaActual = pagina
      this.initialize()
    },
    limpiarFiltros() {
      this.busqueda = ''
      this.filtroServicios = 'todos'
      this.visitaDesde = ''
      this.visitaHasta = ''
      this.paginaActual = 1
      this.initialize()
    },
    nuevoCliente() {
      this.clienteSeleccionado = null
      this.dialogoForm = true
    },
    editarCliente(cliente) {
      this.clienteSeleccionado = cliente
      this.dialogoForm = true
    },
    cerrarFormulario() {
      this.dialogoForm = false
      this.clienteSeleccionado = null
    },
    async guardarCliente(form) {
      this.guardando = true
      try {
        if (this.clienteSeleccionado) {
          await ApiService.clienteEditar(this.clienteSeleccionado._id, form)
        } else {
          await ApiService.clienteNuevo(form)
        }
        const mensaje = this.clienteSeleccionado ? 'Cliente editado con éxito' : 'Cliente agregado con éxito'
        this.cerrarFormulario()
        await this.initialize()
        this.mostrarAviso(mensaje)
      } catch (err) {
        // El servicio ya muestra el detalle del error
      } finally {
        this.guardando = false
      }
    },
    pedirEliminar(cliente) {
      this.clienteSeleccionado = cliente
      this.dialogoEliminar = true
    },
    cerrarEliminar() {
      this.dialogoEliminar = false
      this.clienteSeleccionado = null
    },
    async confirmarEliminar() {
      this.guardando = true
      try {
        const res = await ApiService.deleteClient(this.clienteSeleccionado._id)
        this.cerrarEliminar()
        if (this.clientes.length === 1 && this.paginaActual > 1) this.paginaActual--
        await this.initialize()
        this.mostrarAviso(res?.data?.message || 'Cliente eliminado')
      } catch (err) {
        // El servicio ya muestra el detalle del error
      } finally {
        this.guardando = false
      }
    },
    mostrarAviso(mensaje) {
      this.aviso = mensaje
      clearTimeout(this.avisoTimer)
      this.avisoTimer = setTimeout(() => {
        this.aviso = ''
      }, 4500)
    },
  },
}
</script>


<style scoped>
/* Lo común de los catálogos vive en CatalogoPagina; aquí solo lo propio de
   Clientes (con .cat-pagina delante para ganarle a las reglas comunes). */
.cat-pagina .cat-visita {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-variant-numeric: tabular-nums;
}
.cat-pagina .cat-telefono {
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.cat-pagina .cat-tabla td.cat-celda-alta {
  color: #3d4b5f;
  font-variant-numeric: tabular-nums;
  text-align: right;
  white-space: nowrap;
}
.cat-pagina .cat-direccion-linea {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
@media (max-width: 700px) {
  .cat-pagina .cat-filtro-fechas {
    max-width: calc(100% - 50px);
  }
  .cat-pagina .cat-tabla td.cat-celda-cliente {
    padding-bottom: 8px;
  }
  .cat-pagina .cat-tabla td.cat-celda-direccion,
  .cat-pagina .cat-tabla td.cat-celda-alta {
    color: #526273;
    font-size: 12px;
    text-align: left;
  }
  .cat-pagina .cat-tabla td.cat-celda-alta::before {
    content: 'Alta ';
  }
}
</style>
